import { KeyRound, UserCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGuardians } from '@/features/auth/guardians'
import { toApiError } from '@/api/errors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { RadioGroup } from '@/components/ui/RadioGroup'
import { Select } from '@/components/ui/Select'
import { humanize } from '@/utils/format'
import { useChangeStudentStatus, useCreatePortalAccount } from '../api'
import { STUDENT_STATUSES, type StudentDetail, type StudentStatus } from '../types'

interface Props {
  student: StudentDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}

const statusHelp: Record<StudentStatus, string> = {
  active: 'Studying at the school.',
  inactive: 'Temporarily not attending; the enrollment stays open.',
  transferred: 'Left for another school. Closes the current enrollment.',
  graduated: 'Completed their studies. Closes the current enrollment.',
  expelled: 'Removed by the school. Closes the current enrollment.',
}

export function StatusDialog({ student, open, onOpenChange }: Props) {
  const { t } = useTranslation('students')
  const change = useChangeStudentStatus()
  const [status, setStatus] = useState<StudentStatus>(student.summary.status)

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<UserCheck />}
      title={t('Change student status')}
      description={t('Students who are not active lose access to the portal.')}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('Cancel')}
          </Button>
          <Button
            variant={status === 'expelled' ? 'danger' : 'primary'}
            loading={change.isPending}
            disabled={status === student.summary.status}
            onClick={() =>
              void change.mutateAsync({ id: student.summary.id, status }).then(
                () => {
                  toast.success(
                    t('Status updated'),
                    t('{{name}} is now {{status}}.', {
                      name: student.summary.fullName,
                      status: humanize(status).toLowerCase(),
                    }),
                  )
                  onOpenChange(false)
                },
                (e: unknown) => toast.error(t('Could not update status'), toApiError(e).message),
              )
            }
          >
            {t('Update status')}
          </Button>
        </>
      }
    >
      <RadioGroup
        aria-label={t('Status')}
        appearance="cards"
        value={status}
        onValueChange={(v) => setStatus(v as StudentStatus)}
        options={STUDENT_STATUSES.map((s) => ({
          value: s,
          label: humanize(s),
          description: t(statusHelp[s]),
        }))}
      />
    </Dialog>
  )
}

export function PortalAccountDialog({ student, open, onOpenChange }: Props) {
  const { t } = useTranslation('students')
  const withGuardians = useGuardians()
  const create = useCreatePortalAccount()
  const candidates = [
    ...(student.account
      ? []
      : [{ value: 'student', label: t('{{name}} (student)', { name: student.summary.fullName }) }]),
    ...(withGuardians ? student.guardians : [])
      .filter((g) => !g.hasPortalAccess)
      .map((g) => ({ value: String(g.id), label: `${g.name} (${humanize(g.relation)})` })),
  ]
  const emailFor = (value: string) =>
    value === 'student'
      ? (student.email ?? '')
      : (student.guardians.find((g) => String(g.id) === value)?.email ?? '')
  const [who, setWho] = useState(() => candidates[0]?.value ?? '')
  const [email, setEmail] = useState(() => emailFor(candidates[0]?.value ?? ''))
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setError(null)
    try {
      await create.mutateAsync({
        id: student.summary.id,
        guardianId: who === 'student' ? null : Number(who),
        email,
        password,
      })
      toast.success(t('Portal account created'), t('{{email}} can now sign in to the portal.', { email }))
      onOpenChange(false)
    } catch (e) {
      const err = toApiError(e)
      setError(err.violations[0]?.message ?? err.message)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<KeyRound />}
      title={withGuardians ? t('Create family-portal login') : t('Create student-portal login')}
      description={
        withGuardians
          ? t("Gives the student or a guardian read access to this student's record.")
          : t('Lets the student sign in to the student portal.')
      }
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => void submit()} loading={create.isPending} disabled={!candidates.length}>
            {t('Create account')}
          </Button>
        </>
      }
    >
      {candidates.length === 0 ? (
        <Alert tone="info">
          {withGuardians
            ? t('The student and every guardian already have a portal account.')
            : t('The student already has a portal account.')}
        </Alert>
      ) : (
        <div className="space-y-4">
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <FormField label={t('Account for')} required>
            <Select
              value={who}
              onChange={(e) => {
                setWho(e.target.value)
                setEmail(emailFor(e.target.value))
              }}
              options={candidates}
            />
          </FormField>
          <FormField label={t('Sign-in email')} required>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
          </FormField>
          <FormField
            label={t('Temporary password')}
            required
            hint={t('At least 8 characters. Share it securely; they can change it after signing in.')}
          >
            <Input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          </FormField>
        </div>
      )}
    </Dialog>
  )
}
